import { intlTag } from './locales'
import type { AppLocale } from './types'

/**
 * Dates, numbers and currency.
 *
 * **The rule this file exists to enforce: locale and time zone are independent
 * inputs and are always passed together.** M-03 made the app
 * company-timezone-correct; a bare `toLocaleDateString()` reads the *browser's*
 * zone, so a supervisor in Vancouver reviewing a Toronto plant's shift sees a
 * shift that started "yesterday". That bug is invisible in every test that runs
 * in one zone, which is every test.
 *
 * Every function here therefore takes an explicit `{ locale, timeZone }`. The
 * `no-bare-tolocale` lint rule keeps the direct calls from coming back.
 */

export interface FormatContext {
  readonly locale: AppLocale
  readonly timeZone: string
}

export type DateInput = Date | string | number

/**
 * Named option bags, so ~120 call sites do not each re-decide what "a date"
 * looks like. Raw `Intl.DateTimeFormatOptions` are still accepted where a site
 * genuinely needs something else.
 */
export const DATE_PRESETS = {
  /** `Aug 1` — the dense list/table form. */
  monthDay: { month: 'short', day: 'numeric' },
  /** `Aug 1, 2026` — the default for anything a person will quote. */
  medium: { year: 'numeric', month: 'short', day: 'numeric' },
  /** `1 August 2026` spelled out. */
  long: { year: 'numeric', month: 'long', day: 'numeric' },
  /** `Saturday, 1 August` — schedules and rosters. */
  weekdayLong: { weekday: 'long', month: 'long', day: 'numeric' },
  /** `Sat 1` — compact calendar strips. */
  weekdayShort: { weekday: 'short', day: 'numeric' },
  /** `August 2026` — period headers. */
  monthYear: { month: 'long', year: 'numeric' },
  /** `8/1/2026` — the locale's own numeric shorthand. */
  numeric: { year: 'numeric', month: 'numeric', day: 'numeric' },
  /**
   * `Aug 1, 01:30 PM` — the app's established timestamp shape, for use with
   * `formatDateTime`.
   *
   * It carries its own `hour`/`minute`, which override `formatDateTime`'s
   * default `hm`, because this exact option bag was already the dominant
   * timestamp form in the codebase: five feature slices had independently
   * written `{month:'short', day:'numeric', hour:'2-digit', minute:'2-digit'}`.
   * Keeping `2-digit` rather than "tidying" it to `numeric` is what makes the
   * migration of those sites render byte-identically in English — the e2e
   * suite's text locators are a free regression net only while that holds.
   *
   * Worth noting what those sites were actually doing: they passed `[]` as the
   * locale, which is the VIEWER'S OS locale. Two people looking at the same
   * work order saw different text — `01:30 PM` on a US machine, `01:30 p.m.`
   * on a Canadian one — and neither was the company's choice. Going through
   * this preset makes it deterministic as well as translatable.
   */
  stamp: { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' },
} as const satisfies Record<string, Intl.DateTimeFormatOptions>

/**
 * There is deliberately **no preset reproducing a bare `toLocaleString()`**
 * (`8/1/2026, 7:34:25 PM`).
 *
 * One existed briefly, as scaffolding: it let the ~16 sites that called the
 * bare form migrate onto this layer without changing a pixel, so the diff that
 * fixed *whose* locale and time zone decide a timestamp stayed separable from
 * the diff that changed what people read. Once both had landed (owner decision,
 * 2026-08-01) it had no callers, and a preset with no callers is an invitation
 * to render seconds in a UI that has no use for them.
 *
 * If a site ever genuinely needs that shape — a debug view, a log export — pass
 * the raw `Intl.DateTimeFormatOptions`. Making it a named preset again would
 * make it look recommended.
 */

export const TIME_PRESETS = {
  /** `14:32` / `2:32 PM` — the locale decides the clock. */
  hm: { hour: 'numeric', minute: '2-digit' },
  hms: { hour: 'numeric', minute: '2-digit', second: '2-digit' },
} as const satisfies Record<string, Intl.DateTimeFormatOptions>

export type DatePreset = keyof typeof DATE_PRESETS
export type TimePreset = keyof typeof TIME_PRESETS

/** `2026-08-01` with no time part. */
const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/

/**
 * `new Date('2026-08-01')` is parsed as UTC midnight by spec. Rendered in any
 * negative-offset zone — every North American plant — that is July 31. A
 * calendar date is not an instant: a due date of the 1st is the 1st everywhere,
 * so it is formatted in UTC regardless of the company zone, and an explicit
 * `timeZone` in the caller's options still wins.
 */
function zoneFor(value: DateInput, requested: string, override: string | undefined): string {
  if (override) return override
  return typeof value === 'string' && CALENDAR_DATE.test(value) ? 'UTC' : requested
}

function toDate(value: DateInput): Date {
  return value instanceof Date ? value : new Date(value)
}

// `Intl.DateTimeFormat` construction dominates the cost of formatting; a table
// row formatting 200 timestamps would otherwise build 200 formatters.
const dateTimeFormats = new Map<string, Intl.DateTimeFormat>()
const numberFormats = new Map<string, Intl.NumberFormat>()
const relativeFormats = new Map<string, Intl.RelativeTimeFormat>()

function dateTimeFormatter(locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const cacheKey = `${locale}|${JSON.stringify(options)}`
  let formatter = dateTimeFormats.get(cacheKey)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(intlTag(locale), options)
    dateTimeFormats.set(cacheKey, formatter)
  }
  return formatter
}

function numberFormatter(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const cacheKey = `${locale}|${JSON.stringify(options)}`
  let formatter = numberFormats.get(cacheKey)
  if (!formatter) {
    formatter = new Intl.NumberFormat(intlTag(locale), options)
    numberFormats.set(cacheKey, formatter)
  }
  return formatter
}

function resolveDateOptions(
  preset: DatePreset | Intl.DateTimeFormatOptions | undefined,
  fallback: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormatOptions {
  if (preset === undefined) return fallback
  return typeof preset === 'string' ? DATE_PRESETS[preset] : preset
}

/** A date with no time of day. */
export function formatDate(
  value: DateInput,
  ctx: FormatContext,
  preset?: DatePreset | Intl.DateTimeFormatOptions,
): string {
  const options = resolveDateOptions(preset, DATE_PRESETS.medium)
  return dateTimeFormatter(ctx.locale, {
    ...options,
    timeZone: zoneFor(value, ctx.timeZone, options.timeZone),
  }).format(toDate(value))
}

/** A date and a time of day — the form for anything that happened at an
 * instant (an event, a signature, a reading). */
export function formatDateTime(
  value: DateInput,
  ctx: FormatContext,
  preset?: DatePreset | Intl.DateTimeFormatOptions,
): string {
  const base = resolveDateOptions(preset, DATE_PRESETS.medium)
  const options: Intl.DateTimeFormatOptions = { ...TIME_PRESETS.hm, ...base }
  return dateTimeFormatter(ctx.locale, {
    ...options,
    timeZone: zoneFor(value, ctx.timeZone, options.timeZone),
  }).format(toDate(value))
}

/** A time of day on its own. */
export function formatTime(
  value: DateInput,
  ctx: FormatContext,
  preset?: TimePreset | Intl.DateTimeFormatOptions,
): string {
  const options: Intl.DateTimeFormatOptions =
    preset === undefined ? TIME_PRESETS.hm : typeof preset === 'string' ? TIME_PRESETS[preset] : preset
  return dateTimeFormatter(ctx.locale, {
    ...options,
    timeZone: options.timeZone ?? ctx.timeZone,
  }).format(toDate(value))
}

export function formatNumber(
  value: number,
  ctx: Pick<FormatContext, 'locale'>,
  options: Intl.NumberFormatOptions = {},
): string {
  return numberFormatter(ctx.locale, options).format(value)
}

/**
 * Currency.
 *
 * `currency` is **required and has no default**. `company_settings` carries no
 * currency column — verified 2026-08-01, the only `currency` in the schema is
 * `supplier_quality.*.currency`, per-row and nullable — and inventing one to
 * give this a default would be inventing a company-wide fact from a field that
 * is deliberately per-record. Callers pass the currency that is actually stored
 * with the amount, or the caller has no business rendering a currency symbol.
 */
export function formatCurrency(
  value: number,
  ctx: Pick<FormatContext, 'locale'> & { currency: string },
  options: Omit<Intl.NumberFormatOptions, 'style' | 'currency'> = {},
): string {
  return numberFormatter(ctx.locale, { ...options, style: 'currency', currency: ctx.currency }).format(value)
}

const RELATIVE_UNITS: readonly [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 60 * 60 * 1000],
  ['month', 30 * 24 * 60 * 60 * 1000],
  ['week', 7 * 24 * 60 * 60 * 1000],
  ['day', 24 * 60 * 60 * 1000],
  ['hour', 60 * 60 * 1000],
  ['minute', 60 * 1000],
]

/**
 * `3 days ago` / `in 2 hours`.
 *
 * `now` is injectable so a test can assert the boundary rather than the clock.
 * Anything under a minute renders as the locale's "now"-adjacent phrasing via
 * the `second` unit — deliberately not a hand-written "just now", which would
 * be a hardcoded English string inside the module whose job is to have none.
 */
export function formatRelative(value: DateInput, ctx: Pick<FormatContext, 'locale'>, now: DateInput = new Date()): string {
  let formatter = relativeFormats.get(ctx.locale)
  if (!formatter) {
    formatter = new Intl.RelativeTimeFormat(intlTag(ctx.locale), { numeric: 'auto' })
    relativeFormats.set(ctx.locale, formatter)
  }
  const delta = toDate(value).getTime() - toDate(now).getTime()
  for (const [unit, ms] of RELATIVE_UNITS) {
    if (Math.abs(delta) >= ms) return formatter.format(Math.round(delta / ms), unit)
  }
  return formatter.format(Math.round(delta / 1000), 'second')
}

/**
 * `2024-01-07` was a Sunday, so `7 + index` lands on the wanted weekday.
 *
 * A fixed reference week rather than "today minus its weekday": derived from
 * `new Date()` the result would depend on the day the page was opened, which is
 * the sort of bug that reproduces once a week.
 */
const WEEKDAY_EPOCH_SUNDAY = Date.UTC(2024, 0, 7)

export type WeekdayStyle = 'long' | 'short' | 'narrow'

/**
 * The name of a weekday, by index, where **0 is Sunday**.
 *
 * Written for the same reason `dateOrAbsent` was: eleven files in `apps/web`
 * had independently grown `const WEEKDAYS = ['Sunday', …]` or
 * `['Sun', 'Mon', …]` — the operating-schedule editor, the check-plan calendar,
 * two report schedulers, notification settings, the AI provider schedule, the
 * maintenance and training calendars, the attendance strip. Every one of them
 * was permanently English, and none of them was reachable from a translator.
 *
 * `Intl` already knows these words in every locale the app will ever ship, so
 * putting seven strings in each feature's `copy.ts` would be paying a
 * translator to re-type a table the platform hands out for free — and paying it
 * once per slice. English output is byte-identical (`Sunday`, `Sun`), which is
 * what lets the migration happen under the e2e suite's text locators.
 *
 * The index convention is `Date.getDay()`'s and the contract's
 * (`OperatingWindowInput.weekday`), NOT the locale's first day of the week. A
 * calendar that wants to *start* on Monday reorders the indexes; it does not
 * renumber them.
 */
export function formatWeekday(
  weekday: number,
  ctx: Pick<FormatContext, 'locale'>,
  style: WeekdayStyle = 'long',
): string {
  // Normalized so a negative or out-of-range index cannot produce `Invalid
  // Date` on the screen; the caller's data is a 0–6 contract field either way.
  const index = (((Math.trunc(weekday) % 7) + 7) % 7)
  return dateTimeFormatter(ctx.locale, { weekday: style, timeZone: 'UTC' }).format(
    new Date(WEEKDAY_EPOCH_SUNDAY + index * 86_400_000),
  )
}

/**
 * A list of phrases joined the way the locale joins them.
 *
 * Written for the same reason `formatWeekday` was. `['a photo', 'a note']`
 * was being joined with a hardcoded `' and '` — an English conjunction, in an
 * English position, that no translator can reach. French wants `« et »` and
 * places it the same way here but not for `'or'`; several locales do not use a
 * separator word at all, and the Oxford-comma question is per-locale too.
 * `Intl.ListFormat` knows all of it.
 *
 * `type` defaults to `'conjunction'` (and) — pass `'disjunction'` for "or".
 * English output for a two-item conjunction is `a and b`, byte-identical to
 * the `join(' and ')` it replaces, which is what lets it land under the e2e
 * suite's text locators.
 */
export function formatList(
  items: readonly string[],
  ctx: Pick<FormatContext, 'locale'>,
  type: Intl.ListFormatType = 'conjunction',
): string {
  return new Intl.ListFormat(intlTag(ctx.locale), { style: 'long', type }).format(items)
}

/**
 * A duration in minutes as `2h 15m`.
 *
 * `Intl.DurationFormat` is Baseline-2025 and absent from the Safari versions on
 * the tablets this app targets, so this composes `Intl.NumberFormat` with the
 * unit style instead — which every engine the PWA supports does have.
 */
export function formatDurationMinutes(totalMinutes: number, ctx: Pick<FormatContext, 'locale'>): string {
  const sign = totalMinutes < 0 ? '-' : ''
  const minutes = Math.round(Math.abs(totalMinutes))
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  const hourPart =
    hours > 0 ? numberFormatter(ctx.locale, { style: 'unit', unit: 'hour', unitDisplay: 'narrow' }).format(hours) : ''
  const minutePart =
    rest > 0 || hours === 0
      ? numberFormatter(ctx.locale, { style: 'unit', unit: 'minute', unitDisplay: 'narrow' }).format(rest)
      : ''
  return `${sign}${[hourPart, minutePart].filter(Boolean).join(' ')}`
}

/** How this app spells "there is no value here". */
export const ABSENT = '—'

export interface Formatter {
  readonly locale: AppLocale
  readonly timeZone: string
  /**
   * The BCP-47 tag for `locale` — `'fr'` resolves to `'fr-CA'`.
   *
   * Exposed for the handful of call sites that must hand a tag to a formatter
   * this object does not wrap, because that formatter is **shared with the
   * server**: `formatMetricValue` in `@floorfile/contracts` takes a
   * `locale?: string` so the dashboard screen and the PDF writer run the same
   * code and cannot disagree about what 94.55% rounds to.
   *
   * Passing `format.locale` to those instead is the trap this field exists to
   * close: `Intl` reads a bare `'fr'` as METROPOLITAN French, which formats
   * differently from the Canadian French this app actually ships.
   */
  readonly intlTag: string
  date: (value: DateInput, preset?: DatePreset | Intl.DateTimeFormatOptions) => string
  dateTime: (value: DateInput, preset?: DatePreset | Intl.DateTimeFormatOptions) => string
  time: (value: DateInput, preset?: TimePreset | Intl.DateTimeFormatOptions) => string
  /**
   * A date, or `—` when there isn't one.
   *
   * Exists because four feature slices had independently grown their own
   * `formatDate(iso?: string)` that returned an em dash for `undefined` — and
   * the four had already drifted apart on month style and on whether a
   * calendar date was pinned to UTC. One of them was a day out west of
   * Greenwich as a result.
   *
   * The em dash is not decoration: an absent date and a date of "today" are
   * different facts, and the UI has to be able to tell them apart. Note the
   * limit, so nobody mistakes this for the whole treatment — `Gauge` and
   * `KpiCard` pair their em dash with a *spoken* reason for assistive tech, and
   * a bare dash in a table cell does not. Where the reason is knowable and
   * useful, say it.
   */
  dateOrAbsent: (value: DateInput | null | undefined, preset?: DatePreset | Intl.DateTimeFormatOptions) => string
  dateTimeOrAbsent: (value: DateInput | null | undefined, preset?: DatePreset | Intl.DateTimeFormatOptions) => string
  number: (value: number, options?: Intl.NumberFormatOptions) => string
  /**
   * A money AMOUNT with exactly two decimals and no symbol — the caller's
   * copy places the symbol (fr-CA writes the sign after the number, en before
   * it), because no company-wide currency is stored (see `formatCurrency`).
   * FF-AUDIT-20260914-210459-043: "$6,150.5" and "$0.00" on two screens came
   * from two ad-hoc `number()` calls; this is the one they both use now.
   */
  amount: (value: number) => string
  currency: (value: number, currency: string, options?: Omit<Intl.NumberFormatOptions, 'style' | 'currency'>) => string
  relative: (value: DateInput, now?: DateInput) => string
  durationMinutes: (totalMinutes: number) => string
  /** A weekday name from a 0–6 index, 0 = Sunday. See `formatWeekday`. */
  weekday: (weekday: number, style?: WeekdayStyle) => string
  /** `['a', 'b']` → `a and b`, in the locale's own words. See `formatList`. */
  list: (items: readonly string[], type?: Intl.ListFormatType) => string
}

/**
 * A `Formatter` bound to a locale and a time zone, with no React involved.
 *
 * Lives in the pure half on purpose. `useFormat()` is the way to get one inside
 * a component, but three other callers legitimately have a locale and no hooks:
 * the Worker rendering notification and PDF text for a company whose default
 * locale it looked up (Phase 8), a `lib/` helper called from a mutation
 * callback, and a unit test that wants to assert French output without mounting
 * a provider. Before this existed the only way to build one was to render, so
 * those callers reached for bare `Intl` instead — which is the entire defect
 * class this module was written to remove.
 */
export function createFormatter(ctx: FormatContext): Formatter {
  return {
    locale: ctx.locale,
    timeZone: ctx.timeZone,
    intlTag: intlTag(ctx.locale),
    date: (value, preset) => formatDate(value, ctx, preset),
    dateTime: (value, preset) => formatDateTime(value, ctx, preset),
    time: (value, preset) => formatTime(value, ctx, preset),
    // An empty string counts as absent too: several API fields are
    // `string | null` in the contract but arrive as `''` from a form that
    // submitted a cleared input, and `new Date('')` is an Invalid Date that
    // formats as the literal text "Invalid Date" on the screen.
    dateOrAbsent: (value, preset) =>
      value === null || value === undefined || value === '' ? ABSENT : formatDate(value, ctx, preset),
    dateTimeOrAbsent: (value, preset) =>
      value === null || value === undefined || value === '' ? ABSENT : formatDateTime(value, ctx, preset),
    number: (value, options) => formatNumber(value, ctx, options),
    amount: (value) => formatNumber(value, ctx, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    currency: (value, currency, options) => formatCurrency(value, { locale: ctx.locale, currency }, options),
    relative: (value, now) => formatRelative(value, ctx, now),
    durationMinutes: (totalMinutes) => formatDurationMinutes(totalMinutes, ctx),
    weekday: (weekday, style) => formatWeekday(weekday, ctx, style),
    list: (items, type) => formatList(items, ctx, type),
  }
}

// ── datetime-local ↔ instant, in a named zone ────────────────────────────────
//
// FF-AUDIT-20260914-210459-078: a `datetime-local` value (`YYYY-MM-DDTHH:mm`)
// has no zone. Parsing it with `new Date(value)` reads it in the BROWSER's
// zone and `getHours()` writes it back the same way — while every stamp on a
// record page is formatted in the COMPANY zone. A planned deviation typed as
// 08:00 → 08:00 came back as "12:00 → 12:00". These two functions are the one
// conversion between such an input and the persisted instant, keyed on the
// zone the formatter uses. Same algorithm as the API's `zonedTimeToUtc`.

const ZONE_PARTS: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }

function zoneParts(at: Date, timeZone: string): Record<string, string> {
  return Object.fromEntries(dateTimeFormatter('en', { ...ZONE_PARTS, timeZone }).formatToParts(at).map((part) => [part.type, part.value]))
}

function zoneOffsetMinutes(at: Date, timeZone: string): number {
  const p = zoneParts(at, timeZone)
  const asUtc = Date.UTC(Number(p['year']), Number(p['month']) - 1, Number(p['day']), Number(p['hour']) % 24, Number(p['minute']), Number(p['second']))
  return (asUtc - at.getTime()) / 60_000
}

/** `YYYY-MM-DDTHH:mm` typed in `timeZone` → the persisted ISO instant. Anything else is parsed as-is. */
export function zonedInputToIso(value: string, timeZone: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value)
  if (!match) return new Date(value).toISOString()
  const [, y, mo, d, h, mi, s] = match
  const naive = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s ?? 0))
  let guess = new Date(naive - zoneOffsetMinutes(new Date(naive), timeZone) * 60_000)
  // A first guess can straddle a DST change; recompute at the candidate.
  guess = new Date(naive - zoneOffsetMinutes(guess, timeZone) * 60_000)
  return guess.toISOString()
}

/** A persisted ISO instant → `YYYY-MM-DDTHH:mm` in `timeZone` (what the input shows). */
export function isoToZonedInput(iso: string | undefined, timeZone: string): string {
  if (!iso) return ''
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return ''
  const p = zoneParts(at, timeZone)
  return `${p['year']}-${p['month']}-${p['day']}T${String(Number(p['hour']) % 24).padStart(2, '0')}:${p['minute']}`
}
